/**
 * 存档数据结构
 */
export interface SaveData {
  // 基础游戏状态
  level: number;
  score: number;
  playerHealth: number;

  // 游戏统计
  stats: GameStats;

  // 武器状态
  currentWeapon: string;
  weaponAmmo: { [key: string]: number };

  // 元数据
  timestamp: number;
  playTime: number; // 游戏时间（秒）
  version: string;
}

/**
 * 游戏统计数据
 */
export interface GameStats {
  totalKills: number;
  totalDeaths: number;
  totalShots: number;
  totalHits: number;
  maxLevel: number;
  maxScore: number;
  gamesPlayed: number;
}

/**
 * 存档槽位信息
 */
export interface SaveSlot {
  id: number;
  isEmpty: boolean;
  data?: SaveData;
}

/**
 * 存档管理器
 */
export class SaveManager {
  private static readonly SAVE_KEY_PREFIX = '3d_tank_save_';
  private static readonly STATS_KEY = '3d_tank_global_stats';
  private static readonly VERSION = '1.0.0';
  private static readonly MAX_SLOTS = 3;

  private currentSlot: number = 1;
  private gameStartTime: number = 0;
  private totalPlayTime: number = 0;

  // 全局统计
  private globalStats: GameStats = {
    totalKills: 0,
    totalDeaths: 0,
    totalShots: 0,
    totalHits: 0,
    maxLevel: 1,
    maxScore: 0,
    gamesPlayed: 0
  };

  constructor() {
    this.loadGlobalStats();
    this.gameStartTime = Date.now();
  }

  /**
   * 开始新游戏时调用
   */
  startNewGame(): void {
    this.gameStartTime = Date.now();
    this.totalPlayTime = 0;
    this.globalStats.gamesPlayed++;
    this.saveGlobalStats();
  }

  /**
   * 保存游戏到指定槽位
   */
  save(
    slot: number,
    level: number,
    score: number,
    playerHealth: number,
    currentWeapon: string,
    weaponAmmo: { [key: string]: number }
  ): boolean {
    if (slot < 1 || slot > SaveManager.MAX_SLOTS) {
      console.error(`Invalid save slot: ${slot}`);
      return false;
    }

    // 计算游戏时间
    const currentPlayTime = (Date.now() - this.gameStartTime) / 1000;
    this.totalPlayTime += currentPlayTime;
    this.gameStartTime = Date.now();

    // 更新全局统计
    if (level > this.globalStats.maxLevel) {
      this.globalStats.maxLevel = level;
    }
    if (score > this.globalStats.maxScore) {
      this.globalStats.maxScore = score;
    }

    const saveData: SaveData = {
      level,
      score,
      playerHealth,
      stats: { ...this.globalStats },
      currentWeapon,
      weaponAmmo,
      timestamp: Date.now(),
      playTime: this.totalPlayTime,
      version: SaveManager.VERSION
    };

    try {
      const key = this.getSlotKey(slot);
      localStorage.setItem(key, JSON.stringify(saveData));
      this.saveGlobalStats();
      console.log(`Game saved to slot ${slot}`);
      return true;
    } catch (error) {
      console.error('Failed to save game:', error);
      return false;
    }
  }

  /**
   * 从指定槽位加载游戏
   */
  load(slot: number): SaveData | null {
    if (slot < 1 || slot > SaveManager.MAX_SLOTS) {
      console.error(`Invalid save slot: ${slot}`);
      return null;
    }

    try {
      const key = this.getSlotKey(slot);
      const data = localStorage.getItem(key);

      if (!data) {
        console.log(`No save data in slot ${slot}`);
        return null;
      }

      const saveData: SaveData = JSON.parse(data);

      // 恢复游戏时间
      this.totalPlayTime = saveData.playTime || 0;
      this.gameStartTime = Date.now();
      this.currentSlot = slot;

      console.log(`Game loaded from slot ${slot}`);
      return saveData;
    } catch (error) {
      console.error('Failed to load game:', error);
      return null;
    }
  }

  /**
   * 删除指定槽位的存档
   */
  deleteSave(slot: number): boolean {
    if (slot < 1 || slot > SaveManager.MAX_SLOTS) {
      console.error(`Invalid save slot: ${slot}`);
      return false;
    }

    try {
      const key = this.getSlotKey(slot);
      localStorage.removeItem(key);
      console.log(`Save deleted from slot ${slot}`);
      return true;
    } catch (error) {
      console.error('Failed to delete save:', error);
      return false;
    }
  }

  /**
   * 获取所有存档槽位信息
   */
  getAllSlots(): SaveSlot[] {
    const slots: SaveSlot[] = [];

    for (let i = 1; i <= SaveManager.MAX_SLOTS; i++) {
      const data = this.load(i);
      slots.push({
        id: i,
        isEmpty: data === null,
        data: data || undefined
      });
    }

    return slots;
  }

  /**
   * 检查槽位是否有存档
   */
  hasSlotData(slot: number): boolean {
    const key = this.getSlotKey(slot);
    return localStorage.getItem(key) !== null;
  }

  /**
   * 快速保存到当前槽位
   */
  quickSave(
    level: number,
    score: number,
    playerHealth: number,
    currentWeapon: string,
    weaponAmmo: { [key: string]: number }
  ): boolean {
    return this.save(this.currentSlot, level, score, playerHealth, currentWeapon, weaponAmmo);
  }

  /**
   * 快速加载当前槽位
   */
  quickLoad(): SaveData | null {
    return this.load(this.currentSlot);
  }

  /**
   * 自动保存（用于关卡完成时）
   */
  autoSave(
    level: number,
    score: number,
    playerHealth: number,
    currentWeapon: string,
    weaponAmmo: { [key: string]: number }
  ): boolean {
    // 自动保存到槽位1
    return this.save(1, level, score, playerHealth, currentWeapon, weaponAmmo);
  }

  /**
   * 更新击杀统计
   */
  addKill(): void {
    this.globalStats.totalKills++;
  }

  /**
   * 更新死亡统计
   */
  addDeath(): void {
    this.globalStats.totalDeaths++;
  }

  /**
   * 更新射击统计
   */
  addShot(): void {
    this.globalStats.totalShots++;
  }

  /**
   * 更新命中统计
   */
  addHit(): void {
    this.globalStats.totalHits++;
  }

  /**
   * 获取全局统计数据
   */
  getGlobalStats(): GameStats {
    return { ...this.globalStats };
  }

  /**
   * 获取格式化的游戏时间
   */
  getFormattedPlayTime(): string {
    const currentPlayTime = (Date.now() - this.gameStartTime) / 1000;
    const totalSeconds = Math.floor(this.totalPlayTime + currentPlayTime);

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  /**
   * 获取命中率
   */
  getAccuracy(): number {
    if (this.globalStats.totalShots === 0) return 0;
    return Math.round((this.globalStats.totalHits / this.globalStats.totalShots) * 100);
  }

  /**
   * 格式化存档时间戳
   */
  static formatTimestamp(timestamp: number): string {
    const date = new Date(timestamp);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * 设置当前使用的槽位
   */
  setCurrentSlot(slot: number): void {
    if (slot >= 1 && slot <= SaveManager.MAX_SLOTS) {
      this.currentSlot = slot;
    }
  }

  /**
   * 获取当前槽位
   */
  getCurrentSlot(): number {
    return this.currentSlot;
  }

  /**
   * 获取最大槽位数
   */
  getMaxSlots(): number {
    return SaveManager.MAX_SLOTS;
  }

  // 私有方法

  private getSlotKey(slot: number): string {
    return `${SaveManager.SAVE_KEY_PREFIX}slot_${slot}`;
  }

  private loadGlobalStats(): void {
    try {
      const data = localStorage.getItem(SaveManager.STATS_KEY);
      if (data) {
        this.globalStats = JSON.parse(data);
      }
    } catch (error) {
      console.error('Failed to load global stats:', error);
    }
  }

  private saveGlobalStats(): void {
    try {
      localStorage.setItem(SaveManager.STATS_KEY, JSON.stringify(this.globalStats));
    } catch (error) {
      console.error('Failed to save global stats:', error);
    }
  }

  /**
   * 清除所有存档数据（谨慎使用）
   */
  clearAllData(): void {
    for (let i = 1; i <= SaveManager.MAX_SLOTS; i++) {
      this.deleteSave(i);
    }
    localStorage.removeItem(SaveManager.STATS_KEY);
    this.globalStats = {
      totalKills: 0,
      totalDeaths: 0,
      totalShots: 0,
      totalHits: 0,
      maxLevel: 1,
      maxScore: 0,
      gamesPlayed: 0
    };
    console.log('All save data cleared');
  }
}
